
import React, { useState } from 'react';

interface InquiryFormProps {
  businessName: string;
  formUrl: string;
}

/**
 * InquiryForm – a simple contact form that POSTs URL‑encoded data.
 * Includes basic validation, submission state handling and graceful error handling.
 */
export default function InquiryForm({
  businessName,
  formUrl,
}: InquiryFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic client‑side validation
    if (!formData.name || !formData.email || !formData.message) {
      setErrorMsg('Please fill in all required fields.');
      return;
    }
    if (formData.message.length > 500) {
      setErrorMsg('Message exceeds the maximum length of 500 characters.');
      return;
    }

    setSubmitting(true);
    setErrorMsg(null);

    try {
      const body = new URLSearchParams();
      body.append('name', formData.name);
      body.append('email', formData.email);
      body.append('phone', formData.phone);
      body.append('message', formData.message);
      body.append('business', businessName);

      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (!response.ok) {
        // Throw to be caught by the catch block
        throw new Error(`Server responded with ${response.status}`);
      }

      setSubmitted(true);
    } catch (err) {
      console.error('Inquiry submission failed:', err);
      setErrorMsg('Sorry, something went wrong. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm text-center">
        <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <i className="ri-check-line text-teal-600 text-xl"></i>
        </div>
        <h3 className="text-base font-bold text-gray-900 mb-1">Message Sent!</h3>
        <p className="text-sm text-gray-500">
          Your inquiry has been sent to {businessName}.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
      <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-mail-send-line text-teal-600"></i>
        </div>
        Send an Inquiry
      </h3>

      <form
        id="business-inquiry-form"
        data-readdy-form
        onSubmit={handleSubmit}
        className="space-y-3"
        noValidate
      >
        {/* Hidden field for server‑side processing */}
        <input type="hidden" name="business" value={businessName} />

        <div>
          <input
            type="text"
            name="name"
            placeholder="Your Name *"
            value={formData.name}
            onChange={(e) =>
              setFormData({ ...formData, name: e.target.value })
            }
            required
            className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
          />
        </div>

        <div>
          <input
            type="email"
            name="email"
            placeholder="Email Address *"
            value={formData.email}
            onChange={(e) =>
              setFormData({ ...formData, email: e.target.value })
            }
            required
            className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
          />
        </div>

        <div>
          <input
            type="tel"
            name="phone"
            placeholder="Phone (optional)"
            value={formData.phone}
            onChange={(e) =>
              setFormData({ ...formData, phone: e.target.value })
            }
            className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
          />
        </div>

        <div>
          <textarea
            name="message"
            placeholder="Your Message *"
            rows={4}
            maxLength={500}
            value={formData.message}
            onChange={(e) =>
              setFormData({ ...formData, message: e.target.value })
            }
            required
            className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 resize-none"
          ></textarea>
          <p className="text-xs text-gray-400 mt-1 text-right">
            {formData.message.length}/500
          </p>
        </div>

        {errorMsg && (
          <p className="text-sm text-red-600">{errorMsg}</p>
        )}

        <button
          type="submit"
          disabled={submitting || formData.message.length > 500}
          className="w-full py-2.5 bg-teal-600 text-white text-sm font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {submitting ? 'Sending...' : 'Send Message'}
        </button>
      </form>
    </div>
  );
}
